'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Opportunity {
  id: string;
  title: string;
  category: string;
  location: string;
  commitment: string;
  description: string;
  skills: string[];
  image: string;
  alt: string;
  spots: number;
}

interface VolunteerOpportunitiesProps {
  onApplyClick: (opportunityId: string) => void;
}

const VolunteerOpportunities: React.FC<VolunteerOpportunitiesProps> = ({ onApplyClick }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const opportunities: Opportunity[] = [
  {
    id: 'health-camp',
    title: 'Health Camp Coordinator',
    category: 'Healthcare',
    location: 'Sarthana, Surat',
    commitment: 'Weekend (8 hours)',
    description: 'Help organize and coordinate monthly health camps, assist medical staff, and manage patient registration.',
    skills: ['Communication', 'Organization', 'Basic Medical Knowledge'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_166aaeb81-1766950136073.png",
    alt: 'Healthcare volunteers in blue scrubs organizing medical supplies at community health camp',
    spots: 5
  },
  {
    id: 'food-distribution',
    title: 'Food Distribution Volunteer',
    category: 'Community Service',
    location: 'Varachha, Surat',
    commitment: 'Weekly (4 hours)',
    description: 'Assist in preparing and distributing food packages to underprivileged families in the community.',
    skills: ['Physical Fitness', 'Teamwork', 'Compassion'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cea02904-1764661987443.png",
    alt: 'Volunteers in orange vests distributing food packages to families in community center',
    spots: 10
  },
  {
    id: 'education-mentor',
    title: 'Education Mentor',
    category: 'Education',
    location: 'Kadodara, Surat',
    commitment: 'Bi-weekly (3 hours)',
    description: 'Provide tutoring and mentorship to underprivileged children, help with homework and skill development.',
    skills: ['Teaching', 'Patience', 'Subject Expertise'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9438823-1766877565593.png",
    alt: 'Young female mentor in casual attire teaching group of children at wooden desk with books',
    spots: 8
  },
  {
    id: 'fitness-instructor',
    title: 'Community Fitness Instructor',
    category: 'Health & Wellness',
    location: 'Sarthana, Surat',
    commitment: 'Twice weekly (2 hours)',
    description: 'Lead yoga, exercise, or fitness sessions for community members of all ages and fitness levels.',
    skills: ['Fitness Training', 'Motivation', 'First Aid'],
    image: "https://images.unsplash.com/photo-1586172783888-9c470f5c6468",
    alt: 'Fitness instructor in athletic wear leading outdoor yoga session with diverse group of participants',
    spots: 3
  },
  {
    id: 'social-media',
    title: 'Social Media Advocate',
    category: 'Digital Marketing',
    location: 'Remote',
    commitment: 'Flexible (5 hours/week)',
    description: 'Create engaging content, manage social media campaigns, and help spread awareness about our initiatives.',
    skills: ['Content Creation', 'Social Media', 'Photography'],
    image: "https://images.unsplash.com/photo-1676275774286-ff85f1f81b45",
    alt: 'Young person with laptop and smartphone creating social media content at modern workspace',
    spots: 4
  },
  {
    id: 'event-planning',
    title: 'Event Planning Assistant',
    category: 'Events',
    location: 'Multiple Locations',
    commitment: 'Event-based (Variable)',
    description: 'Support planning and execution of community events, fundraisers, and awareness campaigns.',
    skills: ['Event Management', 'Coordination', 'Creativity'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11a4e5a72-1764666124797.png",
    alt: 'Event planning team arranging colorful decorations and seating for community gathering',
    spots: 6
  }];


  const categories = ['all', 'Healthcare', 'Community Service', 'Education', 'Health & Wellness', 'Digital Marketing', 'Events'];

  const filteredOpportunities = selectedCategory === 'all' ?
  opportunities :
  opportunities.filter((opp) => opp.category === selectedCategory);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-2xl font-heading font-bold text-foreground mb-2">Volunteer Opportunities</h3>
          <p className="text-muted-foreground">Find the perfect way to contribute your time and skills</p>
        </div>
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          {categories.map((category) =>
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all duration-300 ${
            selectedCategory === category ?
            'bg-primary text-primary-foreground shadow-subtle' :
            'bg-muted text-foreground hover:bg-muted/80'}`
            }>

              {category === 'all' ? 'All Categories' : category}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredOpportunities.map((opportunity) =>
        <div
          key={opportunity.id}
          className="bg-card rounded-xl shadow-elevated overflow-hidden hover:shadow-cta transition-all duration-300 group">

            <div className="relative h-48 overflow-hidden">
              <AppImage
              src={opportunity.image}
              alt={opportunity.alt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

              <div className="absolute top-4 right-4 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">
                {opportunity.spots} spots left
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                  {opportunity.category}
                </span>
              </div>

              <h4 className="text-lg font-heading font-bold text-foreground mb-2 line-clamp-1">
                {opportunity.title}
              </h4>

              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {opportunity.description}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm text-foreground">
                  <Icon name="MapPinIcon" size={16} className="text-primary" />
                  <span>{opportunity.location}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-foreground">
                  <Icon name="ClockIcon" size={16} className="text-primary" />
                  <span>{opportunity.commitment}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {opportunity.skills.slice(0, 3).map((skill, index) =>
              <span
                key={index}
                className="text-xs bg-muted text-foreground px-2 py-1 rounded">

                    {skill}
                  </span>
              )}
              </div>

              <button
              onClick={() => onApplyClick(opportunity.id)}
              className="w-full bg-primary text-primary-foreground font-heading font-semibold py-3 rounded-lg hover:bg-primary/90 transition-all duration-300 shadow-subtle hover:shadow-cta flex items-center justify-center space-x-2">

                <span>Apply Now</span>
                <Icon name="ArrowRightIcon" size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {filteredOpportunities.length === 0 &&
      <div className="text-center py-12">
          <Icon name="FaceFrownIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">No opportunities found in this category</p>
        </div>
      }
    </div>);

};

export default VolunteerOpportunities;